import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CoaterLicense } from '@workspace-app/odatabackend/QualicoatBackendModel';

export interface LicenseUpdatePayload {
  withdrawalDate?: string;
  withdrawnBy?: string;
  withdrawalReason?: string;
  expiryDate?: string;
  extensionCount?: number;
  lastExtensionDate?: string;
  // Add other updatable fields as needed
}

// Bulk certificate download types
export interface StartBulkDownloadResponse {
  batchId: string | null;
  totalCertificates: number;
  message?: string;
}

export interface BulkDownloadStatusResponse {
  batchId: string;
  totalCount: number;
  processedCount: number;
  progressPercent: number;
  isComplete: boolean;
  isFailed: boolean;
  error?: string;
  zipBlobRef?: string;
}

export interface BulkDownloadCountResponse {
  count: number;
}

export interface ActivateLicenseResponse {
  licenseNumber: number;
  completeLicenseNumber: string;
  issueDate: string;
  expiryDate: string;
}

export interface NextLicenseNumberResponse {
  nextNumber: number;
  licenseType: string;
}

@Injectable({
  providedIn: 'root',
})
export class LicensesService {
  private http = inject(HttpClient);

  // Subject to notify when licenses data should be reloaded
  private reloadTrigger$ = new Subject<void>();

  // Subject to notify when a specific license is updated (with the updated data)
  private licenseUpdated$ = new Subject<CoaterLicense>();

  // Subject to notify when dialogs should be closed
  private closeDialogs$ = new Subject<void>();

  /**
   * Observable that emits when the licenses table should reload data from server
   */
  get reloadTrigger(): Observable<void> {
    return this.reloadTrigger$.asObservable();
  }

  /**
   * Observable that emits the updated license object
   */
  get licenseUpdated(): Observable<CoaterLicense> {
    return this.licenseUpdated$.asObservable();
  }

  /**
   * Observable that emits when license dialogs should be closed
   */
  get closeDialogs(): Observable<void> {
    return this.closeDialogs$.asObservable();
  }

  /**
   * Update a license and trigger necessary notifications
   */
  updateLicense(licenseId: string, payload: LicenseUpdatePayload): Observable<CoaterLicense> {
    return this.http.put<CoaterLicense>(`/api/licenses/${licenseId}`, payload).pipe(
      tap((updatedLicense) => {
        this.licenseUpdated$.next(updatedLicense);
        this.triggerReload();
      })
    );
  }

  /**
   * Manually trigger a table reload (useful when license is updated externally)
   */
  triggerReload(): void {
    this.reloadTrigger$.next();
  }

  /**
   * Notify that a license was updated (when updated through other means)
   */
  notifyLicenseUpdated(license: CoaterLicense): void {
    this.licenseUpdated$.next(license);
    this.triggerReload();
  }

  /**
   * Withdraw a license using the dedicated withdrawal endpoint.
   */
  withdrawLicense(licenseId: string, withdrawalDate: Date, withdrawalReason: string): Observable<void> {
    // Convert local date to end of day UTC (23:59:59) to preserve the selected date
    const utcDate = new Date(Date.UTC(
      withdrawalDate.getFullYear(),
      withdrawalDate.getMonth(),
      withdrawalDate.getDate(),
      23, 59, 59
    ));

    return this.http
      .post<void>(`/api/licenses/${licenseId}/withdraw`, {
        withdrawalDate: utcDate.toISOString(),
        withdrawalReason,
      })
      .pipe(
        tap(() => {
          this.triggerReload();
          this.closeDialogs$.next();
        })
      );
  }

  /**
   * Suspend a license using the dedicated suspension endpoint.
   */
  suspendLicense(licenseId: string, suspensionReason: string): Observable<void> {
    return this.http
      .post<void>(`/api/licenses/${licenseId}/suspend`, {
        suspensionReason,
      })
      .pipe(
        tap(() => {
          this.triggerReload();
          this.closeDialogs$.next();
        })
      );
  }

  /**
   * Resume a suspended license using the dedicated resume endpoint.
   */
  resumeLicense(licenseId: string, resumptionReason: string): Observable<void> {
    return this.http
      .post<void>(`/api/licenses/${licenseId}/resume`, {
        resumptionReason,
      })
      .pipe(
        tap(() => {
          this.triggerReload();
          this.closeDialogs$.next();
        })
      );
  }

  /**
   * Extend a license
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- missing proper typing for CUD operations
  extendLicense(licenseId: string, newExpiryDate: Date, extensionReason: string): Observable<any> {
    return this.http
      .post(`/api/licenses/${licenseId}/extend-manually`, {
        newExpiryDate: newExpiryDate.toISOString(),
        extensionReason,
      })
      .pipe(tap(() => this.triggerReload()));
  }

  /**
   * Regenerate certificate for a license
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- missing proper typing for CUD operations
  regenerateCertificate(licenseId: string): Observable<any> {
    return this.http
      .post(`/api/licenses/${licenseId}/regenerate-certificate`, {})
      .pipe(tap(() => this.triggerReload()));
  }

  /**
   * Activate a license from granting state
   */
  activateLicense(
    licenseId: string,
    expiryDate: Date,
    licenseNumber?: number,
    activationReason?: string
  ): Observable<ActivateLicenseResponse> {
    return this.http
      .post<ActivateLicenseResponse>(`/api/licenses/${licenseId}/activate`, {
        licenseNumber,
        expiryDate: expiryDate.toISOString(),
        activationReason,
      })
      .pipe(tap(() => this.triggerReload()));
  }

  /**
   * Get the next available license number for a given license type
   */
  getNextLicenseNumber(licenseType: string): Observable<NextLicenseNumberResponse> {
    return this.http.get<NextLicenseNumberResponse>('/api/v1/licenses/next-number', {
      params: { licenseType },
    });
  }

  /**
   * Check if a license number already exists for a given license type
   */
  checkLicenseNumber(licenseType: string, number: number): Observable<{ exists: boolean; isUnique: boolean }> {
    return this.http.get<{ exists: boolean; isUnique: boolean }>(
      `/api/v1/licenses/check-number/${encodeURIComponent(licenseType)}/${number}`
    );
  }

  // ============================================
  // Bulk Certificate Download Methods
  // ============================================

  private baseUrl = '/api/v1/licenses';

  /**
   * Get count of licenses with certificates eligible for bulk download
   */
  getBulkDownloadCertificateCount(certificationYear: number): Observable<BulkDownloadCountResponse> {
    return this.http.get<BulkDownloadCountResponse>(`${this.baseUrl}/bulk-certificate-download/count`, {
      params: {
        certificationYear: certificationYear.toString(),
      },
    });
  }

  /**
   * Start bulk certificate download
   */
  startBulkDownload(certificationYear: number): Observable<StartBulkDownloadResponse> {
    return this.http.post<StartBulkDownloadResponse>(`${this.baseUrl}/bulk-certificate-download`, {
      certificationYear,
    });
  }

  /**
   * Get status of a bulk certificate download operation
   */
  getBulkDownloadStatus(batchId: string): Observable<BulkDownloadStatusResponse> {
    return this.http.get<BulkDownloadStatusResponse>(`${this.baseUrl}/bulk-certificate-download/${batchId}/status`);
  }

  /**
   * Download the zip file for a completed bulk certificate download operation
   * This triggers the browser's file download dialog
   */
  downloadBulkCertificates(batchId: string, filename: string): void {
    this.http
      .get(`${this.baseUrl}/bulk-certificate-download/${batchId}/download`, {
        responseType: 'blob',
        observe: 'response',
      })
      .subscribe((response) => {
        if (response.body) {
          const blob = new Blob([response.body], { type: 'application/zip' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }
      });
  }
}
